import React, { useState, useRef, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ExternalLink, Eye } from "lucide-react";

const GalerieReact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const cabinetImages = [
    { src: "/img/galerie/cabinet-facade.jpg", alt: "Le cabinet" },
    { src: "/img/galerie/salle-attente.jpg", alt: "Salle d'attente" },
    { src: "/img/galerie/salle-consultation.jpg", alt: "Salle de consultation" },
  ];

  const plateauImages = [
    
  ];

  const [activeCategory, setActiveCategory] = useState("cabinet");
  const [selectedImage, setSelectedImage] = useState(null);

  const displayedImages =
    activeCategory === "cabinet" ? cabinetImages : plateauImages;

  // Fermeture avec la touche "Escape"
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setSelectedImage(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <section id="galerie" className="py-20 lg:py-32 bg-gradient-to-br from-warm-50 to-sage-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 lg:mb-20"
        >
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-sage-800 mb-6">
            Galerie du Cabinet
          </h2>
          <p className="text-xl text-sage-600 max-w-3xl mx-auto leading-relaxed">
            Découvrez en images le cabinet. Pour le plateau technique, consultez la visite virtuelle immersive.
          </p>
        </motion.div>

        {/* Boutons de filtre */}
        <div className="flex justify-center mb-12 space-x-4">
          <button
            onClick={() => setActiveCategory("cabinet")}
            className={`px-6 py-2 rounded-full text-sm font-medium transition ${
              activeCategory === "cabinet"
                ? "bg-primary-600 text-white shadow"
                : "bg-white text-sage-700 border border-sage-200 hover:bg-sage-100"
            }`}
          >
            Les locaux
          </button>
          {/*
          <button
            onClick={() => setActiveCategory("plateau")}
            className={`px-6 py-2 rounded-full text-sm font-medium transition ${
              activeCategory === "plateau"
                ? "bg-primary-600 text-white shadow"
                : "bg-white text-sage-700 border border-sage-200 hover:bg-sage-100"
            }`}
          >
            La préparation
          </button>
          */}
        </div>

        {/* Grille d’images */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
          className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        >
          {displayedImages.map((img, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.03 }}
              className="rounded-2xl overflow-hidden shadow-lg group cursor-pointer"
              onClick={() => setSelectedImage(img)}
            >
              <div className="relative h-64">
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-sm px-4 py-2">
                  {img.alt}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Visite virtuelle du plateau technique */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-12"
        >
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 lg:p-12 max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full">
                <Eye className="w-8 h-8 text-white" />
              </div>
            </div>
            <h3 className="font-display text-3xl lg:text-4xl font-bold text-white mb-4">
              Visite virtuelle du plateau technique
            </h3>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto leading-relaxed">
              Découvrez en 3D l’environnement dans lequel vous serez accueillie pour mettre au monde votre bébé.
              Une visite immersive pour vous familiariser avec notre plateau technique.
            </p>
            <motion.a
              href="https://my.matterport.com/show/?m=QUZvsfuQasd"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center bg-white text-primary-700 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Lancer la visite virtuelle
            </motion.a>
          </div>
        </motion.div>
      </div>

      {/* MODALE image plein écran */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[90vh] rounded-xl shadow-xl"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

     
    </section>
  );
};

export default GalerieReact;
